import { ChangeEvent, useEffect, useMemo } from "react";
import styles from "./profile.module.css";
import { useDispatch, useSelector } from "../../services/hooks";
import {
  fetchStatistics,
  resetStatistics,
  setStatisticsPage,
  setStatisticsPageSize,
} from "../../services/statistics/actions";
import {
  getStatisticsCount,
  getStatisticsError,
  getStatisticsItems,
  getStatisticsPage,
  getStatisticsPageSize,
  getStatisticsPending,
} from "../../services/statistics/selectors";

const formatNumber = (value: number): string => {
  if (!Number.isFinite(value)) {
    return "—";
  }

  return Number.isInteger(value) ? String(value) : value.toFixed(2);
};

const formatDate = (value: string): string => {
  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return value || "—";
  }

  const day = String(parsedDate.getDate()).padStart(2, "0");
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const year = parsedDate.getFullYear();
  const hours = String(parsedDate.getHours()).padStart(2, "0");
  const minutes = String(parsedDate.getMinutes()).padStart(2, "0");
  const seconds = String(parsedDate.getSeconds()).padStart(2, "0");

  return `${hours}:${minutes}:${seconds} ${day}-${month}-${year}`;
};

export function ProfileStatistics(): JSX.Element {
  const dispatch = useDispatch();
  const user = useSelector((store) => store.loginReducer.user);
  const userId = user?.id;
  const items = useSelector(getStatisticsItems);
  const count = useSelector(getStatisticsCount);
  const page = useSelector(getStatisticsPage);
  const pageSize = useSelector(getStatisticsPageSize);
  const isLoading = useSelector(getStatisticsPending);
  const error = useSelector(getStatisticsError);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(count / pageSize)),
    [count, pageSize]
  );

  const pageStartItem = count === 0 ? 0 : (page - 1) * pageSize + 1;
  const pageEndItem = Math.min(page * pageSize, count);

  useEffect(() => {
    if (!userId) {
      dispatch(resetStatistics());
      return;
    }

    dispatch(setStatisticsPage(1));
    dispatch(fetchStatistics(userId, 1, pageSize));
  }, [dispatch, pageSize, userId]);

  const onPrevPage = () => {
    if (!userId || page <= 1) {
      return;
    }

    const nextPage = page - 1;
    dispatch(setStatisticsPage(nextPage));
    dispatch(fetchStatistics(userId, nextPage, pageSize));
  };

  const onNextPage = () => {
    if (!userId || page >= totalPages) {
      return;
    }

    const nextPage = page + 1;
    dispatch(setStatisticsPage(nextPage));
    dispatch(fetchStatistics(userId, nextPage, pageSize));
  };

  const onPageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    if (!userId) {
      return;
    }

    const nextPageSize = Number(event.target.value);

    dispatch(setStatisticsPage(1));
    dispatch(setStatisticsPageSize(nextPageSize));
  };

  return (
    <section className={styles.card} aria-label="statistics">
      <div className={styles.header}>
        <h2 className={styles.title}>Statistics</h2>
        <div className={styles.subtitle}>Your activity and progress</div>
      </div>

      {!userId ? (
        <div className={styles.emptyState}>User data is unavailable.</div>
      ) : isLoading ? (
        <div className={styles.emptyState}>Loading statistics...</div>
      ) : error ? (
        <div className={styles.emptyState}>{error}</div>
      ) : items.length === 0 ? (
        <div className={styles.emptyState}>No statistics yet.</div>
      ) : (
        <>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Typing speed</th>
                  <th>Accuracy</th>
                  <th>Error count</th>
                  <th>Error rate</th>
                  <th>Keystrokes</th>
                  <th>Text length</th>
                  <th>Elapsed time</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const key = `${item.user_id}-${item.created_at}-${index}`;

                  return (
                    <tr key={key}>
                      <td>{formatDate(item.created_at)}</td>
                      <td>{formatNumber(item.typing_speed)}</td>
                      <td>{formatNumber(item.accuracy)}</td>
                      <td>{formatNumber(item.error_count)}</td>
                      <td>{formatNumber(item.error_rate)}</td>
                      <td>{formatNumber(item.keystrokes_count)}</td>
                      <td>{formatNumber(item.text_length)}</td>
                      <td>{formatNumber(item.elapsed_time)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className={styles.pagination}>
            <div className={styles.paginationMeta}>
              <div className={styles.paginationInfo}>
                Showing {pageStartItem}-{pageEndItem} of {count}
              </div>
              <label className={styles.pageSizeControl}>
                Rows
                <select
                  className={styles.pageSizeSelect}
                  value={pageSize}
                  onChange={onPageSizeChange}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </label>
            </div>
            <div className={styles.paginationControls}>
              <button
                className={styles.paginationButton}
                onClick={onPrevPage}
                disabled={page <= 1}
                type="button"
              >
                Previous
              </button>
              <span className={styles.paginationPage}>Page {page} / {totalPages}</span>
              <button
                className={styles.paginationButton}
                onClick={onNextPage}
                disabled={page >= totalPages}
                type="button"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}